import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Bell,
  CalendarCheck,
  CalendarX,
  Check,
  CheckCheck,
  ChevronRight,
  CircleAlert,
  CircleCheckBig,
  Eye,
  RefreshCw,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import {
  conversationAttentionQueryKey,
  useConversationAttention,
} from '../shared/conversation-attention'
import type {
  DashboardNotification,
  NotificationFilter,
} from './notification-center-contracts'
import type { NotificationCenterRepository } from './notification-center-repository'
import styles from './NotificationCenter.module.css'

const filters: Array<{ value: NotificationFilter; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'NEEDS_ATTENTION', label: 'Needs attention' },
  { value: 'UNREAD', label: 'Unread' },
]

// Without live hints the badge refreshes on its own at this pace and whenever
// staff act; Overview, when open, every minute, and the open drawer every half
// minute. With live hints all three follow changes (conversation-attention.ts).
const BADGE_REFRESH_MS = 5 * 60_000
const DRAWER_REFRESH_MS = 30_000

function relativeTimestamp(timestamp: number) {
  const deltaSeconds = Math.round((timestamp - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (Math.abs(deltaSeconds) < 60)
    return formatter.format(deltaSeconds, 'second')
  const minutes = Math.round(deltaSeconds / 60)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  const days = Math.round(hours / 24)
  if (Math.abs(days) < 30) return formatter.format(days, 'day')
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(timestamp)
}

function NotificationIcon({ type }: { type: DashboardNotification['type'] }) {
  if (type === 'CONVERSATION_NEEDS_ACTION')
    return (
      <span className={`${styles.notificationIcon} ${styles.actionIcon}`}>
        <CircleAlert size={18} aria-hidden="true" />
      </span>
    )
  if (type === 'CONVERSATION_NEEDS_REVIEW')
    return (
      <span className={styles.notificationIcon}>
        <Eye size={18} aria-hidden="true" />
      </span>
    )
  if (type === 'CONVERSATION_ACTION_COMPLETED')
    return (
      <span className={`${styles.notificationIcon} ${styles.settledIcon}`}>
        <CircleCheckBig size={18} aria-hidden="true" />
      </span>
    )
  if (type === 'CONVERSATION_REVIEWED')
    return (
      <span className={`${styles.notificationIcon} ${styles.reviewedIcon}`}>
        <CheckCheck size={18} aria-hidden="true" />
      </span>
    )
  return (
    <span className={styles.notificationIcon}>
      {type === 'APPOINTMENT_CANCELLED' ? (
        <CalendarX size={18} aria-hidden="true" />
      ) : (
        <CalendarCheck size={18} aria-hidden="true" />
      )}
    </span>
  )
}

// Appointment updates carry per-user read state; a conversation's
// notification is read exactly when its work is settled.
function isUpdate(notification: DashboardNotification) {
  return notification.routeTarget.type === 'APPOINTMENTS'
}

export function NotificationCenter({
  tenantId,
  repository,
}: {
  tenantId: string | null
  repository: NotificationCenterRepository
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<NotificationFilter>('ALL')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const queryKey = ['dashboard-notifications', tenantId, filter] as const
  // The list is read only for the open drawer, fresh each time it opens.
  const feed = useInfiniteQuery({
    queryKey,
    enabled: Boolean(tenantId) && open,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      repository.getFeed(tenantId as string, filter, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
  const attention = useConversationAttention(
    (id) => repository.getAttention(id),
    tenantId,
    open ? DRAWER_REFRESH_MS : BADGE_REFRESH_MS
  )
  // The open list is never older than the count beside it: whenever the
  // shared count refreshes (the drawer's own refresh, an Overview poll, or a
  // staff command anywhere in the Dashboard), the list is read again. A
  // failed read counts as the list's latest, so a failure is shown with its
  // Retry instead of being re-read in a loop.
  const listBehind =
    open &&
    !feed.isFetching &&
    attention.dataUpdatedAt > 0 &&
    attention.dataUpdatedAt > Math.max(feed.dataUpdatedAt, feed.errorUpdatedAt)
  useEffect(() => {
    if (!listBehind) return
    void queryClient.invalidateQueries({
      queryKey: ['dashboard-notifications', tenantId],
    })
  }, [listBehind, queryClient, tenantId])
  const invalidateFeed = () =>
    queryClient.invalidateQueries({
      queryKey: ['dashboard-notifications', tenantId],
    })
  const markRead = useMutation({
    mutationFn: (notificationId: string) =>
      repository.markRead(tenantId as string, notificationId),
    onSuccess: invalidateFeed,
  })
  const dismiss = useMutation({
    mutationFn: (notificationId: string) =>
      repository.dismiss(tenantId as string, notificationId),
    onSuccess: invalidateFeed,
  })
  const markAllRead = useMutation({
    mutationFn: () => repository.markAllRead(tenantId as string),
    onSuccess: invalidateFeed,
  })
  const firstPage = feed.data?.pages[0]
  const unreadCount = firstPage?.unreadCount
  // The badge and the Needs attention count are Overview's number.
  const outstandingCount = attention.data?.outstandingCount
  const notifications =
    feed.data?.pages.flatMap((page) => page.notifications) ?? []
  const triggerLabel = !tenantId
    ? 'Notifications'
    : attention.isError
      ? 'Notifications unavailable'
      : attention.isPending
        ? 'Notifications loading'
        : `Notifications, ${outstandingCount} ${outstandingCount === 1 ? 'needs' : 'need'} attention`

  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    const trigger = triggerRef.current
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      )
      if (!focusable.length) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
      trigger?.focus()
    }
  }, [open])

  const openTarget = (notification: DashboardNotification) => {
    if (isUpdate(notification) && !notification.readAtMs)
      markRead.mutate(notification.notificationId)
    setOpen(false)
    const routeTarget = notification.routeTarget
    void navigate(
      routeTarget.type === 'CONVERSATIONS'
        ? routeTarget.interactionAggregateId
          ? `/conversations?aggregate=${encodeURIComponent(routeTarget.interactionAggregateId)}`
          : '/conversations'
        : '/appointments'
    )
  }

  return (
    <>
      <button
        ref={triggerRef}
        className={styles.trigger}
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="notification-center"
        disabled={!tenantId}
        onClick={() => {
          setOpen(true)
          void queryClient.invalidateQueries({
            queryKey: ['dashboard-notifications', tenantId],
          })
          void queryClient.invalidateQueries({
            queryKey: conversationAttentionQueryKey(tenantId),
          })
        }}
      >
        <Bell size={17} strokeWidth={1.75} aria-hidden="true" />
        {attention.isError ? (
          <span className={styles.badge} aria-hidden="true">
            !
          </span>
        ) : outstandingCount !== undefined && outstandingCount > 0 ? (
          <span className={styles.badge} aria-hidden="true">
            {outstandingCount > 99 ? '99+' : outstandingCount}
          </span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div className={styles.layer}>
              <button
                className={styles.backdrop}
                type="button"
                aria-label="Close notifications"
                onClick={() => setOpen(false)}
              />
              <section
                ref={panelRef}
                id="notification-center"
                className={styles.panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="notification-center-title"
              >
                <header className={styles.panelHeader}>
                  <div>
                    <h2 id="notification-center-title">Notifications</h2>
                    <p>Recent clinic activity from verified Flio records.</p>
                  </div>
                  <button
                    ref={closeRef}
                    className={styles.iconButton}
                    type="button"
                    aria-label="Close notifications"
                    onClick={() => setOpen(false)}
                  >
                    <X size={18} aria-hidden="true" />
                  </button>
                </header>
                <div className={styles.toolbar}>
                  <div
                    className={styles.filters}
                    aria-label="Notification filters"
                  >
                    {filters.map((option) => (
                      <button
                        key={option.value}
                        className={styles.filter}
                        type="button"
                        aria-pressed={filter === option.value}
                        onClick={() => setFilter(option.value)}
                      >
                        {option.label}
                        {option.value === 'NEEDS_ATTENTION' &&
                        outstandingCount !== undefined ? (
                          <span>{outstandingCount}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                  <button
                    className={styles.markAll}
                    type="button"
                    disabled={
                      unreadCount === undefined ||
                      unreadCount === 0 ||
                      markAllRead.isPending
                    }
                    onClick={() => markAllRead.mutate()}
                  >
                    <Check size={14} aria-hidden="true" />
                    Mark all read
                  </button>
                </div>
                <div className={styles.content} aria-live="polite">
                  {feed.isPending ? (
                    <div
                      className={styles.loading}
                      aria-label="Loading notifications"
                    >
                      {[0, 1, 2].map((item) => (
                        <div className={styles.skeletonRow} key={item}>
                          <span />
                          <div>
                            <i />
                            <i />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : feed.isError ? (
                    <div className={styles.state} role="alert">
                      <Bell size={24} aria-hidden="true" />
                      <strong>Notifications are unavailable</strong>
                      <p>
                        Your clinic data was not changed. Try the read again.
                      </p>
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => void feed.refetch()}
                      >
                        <RefreshCw size={15} aria-hidden="true" />
                        Retry
                      </Button>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className={styles.state}>
                      <Check size={24} aria-hidden="true" />
                      <strong>
                        {filter === 'ALL'
                          ? 'No notifications yet'
                          : filter === 'NEEDS_ATTENTION'
                            ? 'Nothing needs attention'
                            : 'Nothing in this view'}
                      </strong>
                      <p>
                        {filter === 'ALL'
                          ? 'Verified clinic activity will appear here.'
                          : filter === 'NEEDS_ATTENTION'
                            ? 'Every request is completed and every conversation reviewed.'
                            : 'Try another filter to see recent activity.'}
                      </p>
                    </div>
                  ) : (
                    <ul className={styles.list}>
                      {notifications.map((notification) => (
                        <li
                          className={
                            notification.readAtMs ? styles.read : styles.unread
                          }
                          key={notification.notificationId}
                        >
                          <NotificationIcon type={notification.type} />
                          <div className={styles.notificationBody}>
                            <div className={styles.notificationHeading}>
                              <strong>{notification.title}</strong>
                              <time
                                dateTime={new Date(
                                  notification.occurredAtMs
                                ).toISOString()}
                              >
                                {relativeTimestamp(notification.occurredAtMs)}
                              </time>
                            </div>
                            <p>{notification.description}</p>
                            <div className={styles.rowActions}>
                              <button
                                type="button"
                                onClick={() => openTarget(notification)}
                              >
                                {notification.routeTarget.type ===
                                'CONVERSATIONS'
                                  ? 'Open conversation'
                                  : 'View appointments'}
                                <ChevronRight size={14} aria-hidden="true" />
                              </button>
                              {isUpdate(notification) &&
                              !notification.readAtMs ? (
                                <button
                                  type="button"
                                  disabled={markRead.isPending}
                                  onClick={() =>
                                    markRead.mutate(notification.notificationId)
                                  }
                                >
                                  Mark read
                                </button>
                              ) : null}
                              {notification.dismissible ? (
                                <button
                                  type="button"
                                  disabled={dismiss.isPending}
                                  onClick={() =>
                                    dismiss.mutate(notification.notificationId)
                                  }
                                >
                                  Dismiss
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {feed.hasNextPage ? (
                    <Button
                      className={styles.loadMore}
                      variant="secondary"
                      type="button"
                      disabled={feed.isFetchingNextPage}
                      onClick={() => void feed.fetchNextPage()}
                    >
                      {feed.isFetchingNextPage ? 'Loading…' : 'Load older'}
                    </Button>
                  ) : null}
                </div>
              </section>
            </div>,
            document.body
          )
        : null}
    </>
  )
}
